// A child's controls and the trip as they may see it (domain/controls, domain/trip/visible).
// Grown-ups always get the whole trip, players or not (WI-37).
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ChildSettings } from '../../shared/controls'
import type { Person } from '../../shared/events'
import type { Pack, Stage } from '../../shared/pack/schema'
import { settingsForPlayer } from '../../shared/controls'
import { settingsFor } from '../domain/controls'
import { crateNew, myPlaces } from '../domain/places'
import { tripNow } from '../../shared/tripZone'
import { surprisesJustOpened, unwrapDue } from '../domain/trip/unwrap'
import { revealed, stageReveals, visibleTrip, type TripClock } from '../domain/trip/visible'
import { kvGet, kvSet } from './db'
import { useEvents } from './progress'

export function useChildSettings(child: Pick<Person, 'id' | 'ageBand'> | null): ChildSettings | null {
  const events = useEvents()
  const mine = useMemo(() => events.filter((e) => e.type === 'child.settings.set'), [events])
  const key = mine.map((e) => `${e.id}:${e.seq ?? ''}`).join(',')
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` stands for `mine`
  return useMemo(() => (child ? settingsFor(child, mine) : null), [child?.id, child?.ageBand, key])
}

/** A player's settings (WI-37): a child's controls, a grown-up player's adult defaults, or null for someone who doesn't play. */
export function usePlayerSettings(p: Pick<Person, 'id' | 'kind' | 'ageBand' | 'plays'> | null): ChildSettings | null {
  const events = useEvents()
  const mine = useMemo(() => events.filter((e) => e.type === 'child.settings.set'), [events])
  const key = mine.map((e) => `${e.id}:${e.seq ?? ''}`).join(',')
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` stands for `mine`
  return useMemo(() => (p ? settingsForPlayer(p, mine) : null), [p?.id, p?.kind, p?.ageBand, p?.plays, key])
}

/** The trip clock, ticking each minute, in the zone of the stage under way (B13). */
export function useTripClock(pack: Pack): TripClock {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000)
    return () => clearInterval(id)
  }, [])
  const clock = tripNow(pack, now)
  return useMemo(() => ({ date: clock.date, time: clock.time }), [clock.date, clock.time])
}

export function useVisibleTrip(pack: Pack, person: Person): Pack {
  const settings = useChildSettings(person.kind === 'child' ? person : null)
  const clock = useTripClock(pack)
  const v = settings?.visibility
  // Only re-mask when something actually opens (or the day turns), not every minute.
  const opened = useMemo(() => {
    if (!v) return 0
    const marks = [...stageReveals(pack.itinerary.stages, v).values(), ...pack.itinerary.dayItems.map((i) => i.surprise?.until ?? null)]
    return marks.filter((m) => m && revealed(m, clock)).length
  }, [pack, v, clock])
  const vKey = JSON.stringify(v ?? null)
  return useMemo(
    () => (v ? visibleTrip(pack, v, clock) : pack),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run on a reveal or a new day, not each minute
    [pack, vKey, opened, clock.date],
  )
}

/**
 * The unwrap moment due for this child (A3, domain/trip/unwrap): a stage that was wrapped for them
 * and has opened. Marked seen as soon as it shows (kv `unwrapSeen:<child>`), so it plays once
 * even if it's skipped or the app is closed mid-way. Grown-ups never see wrapped stages.
 */
export function useUnwrap(pack: Pack, person: Person): { stage: Stage | null; done(): void } {
  const settings = useChildSettings(person.kind === 'child' ? person : null)
  const clock = useTripClock(pack)
  const [stage, setStage] = useState<Stage | null>(null)
  const v = settings?.visibility
  const vKey = JSON.stringify(v ?? null)
  useEffect(() => {
    if (!v) return
    let gone = false
    const key = `unwrapSeen:${person.id}`
    void kvGet<string[]>(key).then((seen = []) => {
      if (gone) return
      const due = unwrapDue(pack.itinerary.stages, v, clock, new Set(seen))
      if (!due.seen.length) return
      void kvSet(key, [...seen, ...due.seen])
      if (due.show) setStage(due.show)
    })
    return () => {
      gone = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-check on a new reveal minute, settings or trip
  }, [pack.itinerary.stages, vKey, clock.date, clock.time, person.id])
  const done = useCallback(() => setStage(null), [])
  return { stage, done }
}

/**
 * Surprise plans that opened today and haven't burst open on this phone yet (Trip · Today's
 * small unwrap). They're marked seen once shown (kv `surpriseSeen:<person>`).
 */
export function useSurpriseBursts(pack: Pack, person: Person): ReadonlySet<string> {
  const clock = useTripClock(pack)
  const [burst, setBurst] = useState<ReadonlySet<string>>(new Set())
  const items = pack.itinerary.dayItems
  useEffect(() => {
    let gone = false
    const key = `surpriseSeen:${person.id}`
    void kvGet<string[]>(key).then((seen = []) => {
      if (gone) return
      const fresh = surprisesJustOpened(items, clock, new Set(seen))
      if (!fresh.length) return
      void kvSet(key, [...seen, ...fresh])
      setBurst(new Set(fresh))
    })
    return () => {
      gone = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-check each minute, not on a new clock object
  }, [items, clock.date, clock.time, person.id])
  return burst
}

/**
 * How many places are new in the Crate since this person last opened it on this phone (the
 * dock's Crate badge, domain/places crateNew). While the Crate is `open`, everything found counts
 * as seen (kv `crateSeen:<person>`, like the unwrap and surprise marks above).
 */
export function useCrateNew(pack: Pack, person: Person, open: boolean): number {
  const events = useEvents()
  const foundKey = [...myPlaces(events, person.id).found].sort().join(',')
  const tripKey = pack.places.map((p) => p.id).join(',')
  const key = `crateSeen:${person.id}`
  // What was seen, for whose key (another person's list never counts for this one).
  const [state, setState] = useState<{ key: string; seen: string[] } | null>(null)
  const seen = state?.key === key ? state.seen : null
  useEffect(() => {
    let gone = false
    void kvGet<string[]>(key).then((s = []) => {
      if (!gone) setState({ key, seen: s })
    })
    return () => {
      gone = true
    }
  }, [key])
  const found = foundKey ? foundKey.split(',') : []
  const fresh = seen ? crateNew(new Set(found), tripKey ? tripKey.split(',') : [], seen) : []
  const freshKey = fresh.join(',')
  useEffect(() => {
    if (!open || !seen || !freshKey) return
    const next = [...new Set([...seen, ...freshKey.split(',')])]
    void kvSet(key, next).then(() => setState({ key, seen: next }))
  }, [open, seen, freshKey, key])
  return open ? 0 : fresh.length
}
